// filepath: c:\Users\marratibel\Documents\GitHub\VentaDirecta\tattersall-venta-usados\src\pages\Quotes\components\QuoteDetailModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Divider,
  Avatar,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Paper,
  Fade,
  useTheme,
  useMediaQuery,
  alpha,
  LinearProgress,
  Badge,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  Close,
  Person,
  Phone,
  Email,
  Business,
  CalendarToday,
  Update,
  WhatsApp,
  PictureAsPdf,
  OpenInNew,
  CheckCircle,
  Warning,
  TrendingUp,
  AccessTime,
  LocalOffer,
  Category,
  LocationOn,
  DirectionsCar,
  Share,
  Message,
  Flag,
  History,
  ExpandMore,
  ExpandLess,
  Bolt,
  Timer,
  TrendingDown,
  AttachMoney,
  Repeat,
  Star,
  NewReleases,
  CheckCircleOutline,
  Cancel,
  Undo,
  RemoveCircleOutline,
  Replay,
  LowPriority,
  PriorityHigh,
  Pause,
  PlayArrow,
  FlagOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/context";
import {
  QUOTE_STATUSES,
  PRIORITIES,
} from "../../../constants/QuotesConstants.jsx";
import { getWhatsAppURL } from "../../../constants/contactInfo";
import productsService from "../../../services/productsService";
import CopyButton from "../../../components/common/CopyButton";
import imagePlaceholder from "../../../assets/image-placeholder.webp";
import SmartImage from "../../../components/common/SmartImage";
import {
  normalizeServerDate,
  formatServerDate,
  getTimeElapsed,
  calculateSLA,
  calculateUrgency,
} from "../../../utils/dateUtils";

/**
 * 🎨 Modal de Detalle de Cotización - Diseño Mejorado para Backoffice
 *
 * Features para Administradores:
 * - Header con gradiente y badges de estado/prioridad
 * - Imagen del producto prominente con estado de disponibilidad
 * - Indicador de urgencia inteligente
 * - Acciones rápidas de contacto (WhatsApp, Email, Llamar)
 * - ✨ NUEVO: Contra-acciones (reabrir, desmarcar urgente, etc.)
 * - Historial de cotizaciones del cliente
 * - Métricas del cliente (total cotizado, frecuencia)
 * - Controles de estado y prioridad
 * - Diseño responsive
 * - ✨ NUEVO: Fechas normalizadas (servidor tiene 3 horas de retraso)
 */
export const QuoteDetailModal = ({
  open,
  onClose,
  quote,
  onStatusChange,
  onPriorityChange,
  isBackoffice = false,
  clientHistory = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { token } = useAuth();

  // Estados locales
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [productSoldStatus, setProductSoldStatus] = useState(false);
  const [checkingProduct, setCheckingProduct] = useState(false);
  const [localStatus, setLocalStatus] = useState(quote?.status || "pending");
  const [localPriority, setLocalPriority] = useState(
    quote?.priority || "normal"
  );
  const [updating, setUpdating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [quickActionLoading, setQuickActionLoading] = useState(null);

  // ═══════════════════════════════════════════════════════════════════
  // 🔄 Determinar estados para mostrar acciones/contra-acciones
  // ═══════════════════════════════════════════════════════════════════
  const isUrgent = localPriority === "urgent";
  const isHighPriority = localPriority === "high";
  const isPending = localStatus === "pending";
  const isInProgress = localStatus === "in_progress";
  const isCompleted = localStatus === "completed";
  const isCancelled = localStatus === "cancelled";
  const isRejected = localStatus === "rejected";
  const isClosed = isCompleted || isCancelled || isRejected;

  // Actualizar estados locales cuando cambie la quote
  useEffect(() => {
    if (quote) {
      setLocalStatus(quote.status || "pending");
      setLocalPriority(quote.priority || "normal");
      setProductSoldStatus(quote.product_data?.is_sold || false);
    }
  }, [quote]);

  // Verificar si el producto sigue disponible
  useEffect(() => {
    const checkProductStatus = async () => {
      if (!quote?.product_data?.id || quote.product_data?.is_sold) return;

      try {
        setCheckingProduct(true);
        const product = await productsService.getProductById(
          quote.product_data.id
        );
        if (product?.is_sold) {
          setProductSoldStatus(true);
        }
      } catch (error) {
        console.error("Error verificando producto:", error);
      } finally {
        setCheckingProduct(false);
      }
    };

    if (open && quote) {
      checkProductStatus();
    }
  }, [open, quote]);

  // Configuración de estado y prioridad
  const statusConfig = QUOTE_STATUSES[localStatus] || QUOTE_STATUSES.pending;
  const priorityConfig = PRIORITIES[localPriority] || PRIORITIES.normal;

  // Calcular métricas del cliente (solo para backoffice)
  const clientMetrics = useMemo(() => {
    if (!isBackoffice || !clientHistory || clientHistory.length === 0) {
      return null;
    }

    const totalQuotes = clientHistory.length;
    const totalValue = clientHistory.reduce((sum, q) => {
      return sum + (q.product_data?.precio || 0);
    }, 0);

    const completedQuotes = clientHistory.filter(
      (q) => q.status === "completed"
    ).length;
    const conversionRate =
      totalQuotes > 0 ? (completedQuotes / totalQuotes) * 100 : 0;

    const isRecurring = totalQuotes > 1;
    const isHighValue = totalValue > 50000000;

    return {
      totalQuotes,
      totalValue,
      completedQuotes,
      conversionRate,
      isRecurring,
      isHighValue,
      otherQuotes: clientHistory.filter((q) => q.id !== quote?.id).slice(0, 5),
    };
  }, [clientHistory, isBackoffice, quote?.id]);

  // ═══════════════════════════════════════════════════════════════════
  // 🕐 Calcular información de tiempo con normalización del servidor
  // ═══════════════════════════════════════════════════════════════════
  const timeInfo = useMemo(() => {
    if (!quote?.created_at) return null;

    const created = normalizeServerDate(quote.created_at);
    const updated = quote.updated_at
      ? normalizeServerDate(quote.updated_at)
      : created;

    const elapsed = getTimeElapsed(quote.created_at);

    // Si elapsed es null, retornar valores por defecto
    if (!elapsed) {
      return {
        created,
        updated,
        diffDays: 0,
        diffHours: 0,
        diffMinutes: 0,
        urgencyLevel: "low",
        urgencyColor: theme.palette.success.main,
        urgencyLabel: "Nueva",
        urgencyScore: 0,
        slaPercentage: 0,
        slaBreached: false,
        hoursElapsed: 0,
        formattedCreated: "No disponible",
        formattedUpdated: "No disponible",
        relativeTime: "Ahora",
      };
    }

    const sla = calculateSLA(quote.created_at);
    const urgency = calculateUrgency(quote.created_at, localStatus);

    return {
      created,
      updated,
      diffDays: elapsed.days || 0,
      diffHours: elapsed.hours || 0,
      diffMinutes: elapsed.minutes || 0,
      urgencyLevel: urgency?.level || "low",
      urgencyColor:
        urgency?.level === "critical"
          ? theme.palette.error.main
          : urgency?.level === "high"
          ? theme.palette.warning.main
          : urgency?.level === "medium"
          ? theme.palette.info.main
          : theme.palette.success.main,
      urgencyLabel: urgency?.label || "Nueva",
      urgencyScore: urgency?.score || 0,
      slaPercentage: sla?.percentage || 0,
      slaBreached: sla?.isBreached || false,
      hoursElapsed: elapsed.totalHours || 0,
      formattedCreated: formatServerDate(quote.created_at, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      formattedUpdated: formatServerDate(quote.updated_at || quote.created_at, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      relativeTime: elapsed.relativeText || "Ahora",
    };
  }, [quote, localStatus, theme]);

  // Formatear precio
  const formatPrice = (price) => {
    if (!price) return "Precio a consultar";
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Obtener imagen del producto
  const getProductImage = () => {
    if (quote?.product_data?.image) return quote.product_data.image;
    if (quote?.product_data?.images?.length > 0)
      return quote.product_data.images[0];
    return imagePlaceholder;
  };

  // Handlers
  const handleDownloadPdf = async () => {
    if (!token) {
      setPdfError("Debes iniciar sesión para descargar el PDF");
      return;
    }

    try {
      setDownloadingPdf(true);
      setPdfError(null);
      await productsService.quotes.downloadAndSavePdf(
        quote.id,
        quote.quote_id,
        token
      );
    } catch (error) {
      console.error("Error descargando PDF:", error);
      setPdfError(error.message || "Error al descargar el PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleNavigateToProduct = () => {
    if (quote?.product_data?.slug && !productSoldStatus) {
      navigate(`/producto/${quote.product_data.slug}`);
      onClose();
    }
  };

  const handleWhatsAppClient = () => {
    const phone = quote?.client_phone?.replace(/[\s+()-]/g, "");
    if (phone) {
      const message = `Hola ${
        quote.client_name
      }, le escribo en relación a su cotización ${quote.quote_id} sobre ${
        quote.product_data?.name || "el producto consultado"
      }. `;
      window.open(
        `https://wa.me/${phone}?text=${encodeURIComponent(message)}`,
        "_blank"
      );
    }
  };

  const handleEmailClient = () => {
    if (quote?.client_email) {
      const subject = encodeURIComponent(`Re: Cotización ${quote.quote_id}`);
      const body = encodeURIComponent(
        `Estimado/a ${quote.client_name},\n\nEn relación a su cotización ${
          quote.quote_id
        } sobre ${quote.product_data?.name || "el producto consultado"}...\n\n`
      );
      window.open(
        `mailto:${quote.client_email}?subject=${subject}&body=${body}`,
        "_blank"
      );
    }
  };

  const handleCallClient = () => {
    if (quote?.client_phone) {
      window.open(
        `tel:${quote.client_phone.replace(/[\s+()-]/g, "")}`,
        "_blank"
      );
    }
  };

  const handleStatusChange = async (event) => {
    const newStatus = event.target.value;
    setLocalStatus(newStatus);
    if (onStatusChange) {
      setUpdating(true);
      try {
        await onStatusChange(quote.id, newStatus);
      } finally {
        setUpdating(false);
      }
    }
  };

  const handlePriorityChange = async (event) => {
    const newPriority = event.target.value;
    setLocalPriority(newPriority);
    if (onPriorityChange) {
      setUpdating(true);
      try {
        await onPriorityChange(quote.id, newPriority);
      } finally {
        setUpdating(false);
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // ⚡ ACCIONES RÁPIDAS Y CONTRA-ACCIONES
  // ═══════════════════════════════════════════════════════════════════
  const handleQuickAction = async (action) => {
    setQuickActionLoading(action);
    try {
      switch (action) {
        // ══════════════════════════════════════════════════════════════
        // 📋 ACCIONES DE ESTADO
        // ══════════════════════════════════════════════════════════════
        case "mark_contacted":
          if (onStatusChange) {
            await onStatusChange(quote.id, "contacted");
            setLocalStatus("contacted");
          }
          break;
        case "start_progress":
          if (onStatusChange) {
            await onStatusChange(quote.id, "in_progress");
            setLocalStatus("in_progress");
          }
          break;
        case "mark_completed":
          if (onStatusChange) {
            await onStatusChange(quote.id, "completed");
            setLocalStatus("completed");
          }
          break;
        case "mark_rejected":
          if (onStatusChange) {
            await onStatusChange(quote.id, "rejected");
            setLocalStatus("rejected");
          }
          break;
        case "mark_cancelled":
          if (onStatusChange) {
            await onStatusChange(quote.id, "cancelled");
            setLocalStatus("cancelled");
          }
          break;

        // ══════════════════════════════════════════════════════════════
        // 🔄 CONTRA-ACCIONES DE ESTADO (Reabrir, Pausar, etc.)
        // ══════════════════════════════════════════════════════════════
        case "reopen":
          // Contra-acción de completed/rejected/cancelled -> pending
          if (onStatusChange) {
            await onStatusChange(quote.id, "pending");
            setLocalStatus("pending");
          }
          break;
        case "pause_to_pending":
          // Contra-acción de in_progress -> pending
          if (onStatusChange) {
            await onStatusChange(quote.id, "pending");
            setLocalStatus("pending");
          }
          break;

        // ══════════════════════════════════════════════════════════════
        // 🔥 ACCIONES DE PRIORIDAD
        // ══════════════════════════════════════════════════════════════
        case "escalate":
          if (onPriorityChange) {
            await onPriorityChange(quote.id, "urgent");
            setLocalPriority("urgent");
          }
          break;
        case "mark_high":
          if (onPriorityChange) {
            await onPriorityChange(quote.id, "high");
            setLocalPriority("high");
          }
          break;

        // ══════════════════════════════════════════════════════════════
        // 🔄 CONTRA-ACCIONES DE PRIORIDAD (Quitar urgencia, normalizar)
        // ══════════════════════════════════════════════════════════════
        case "unmark_urgent":
          // Contra-acción de urgent -> normal
          if (onPriorityChange) {
            await onPriorityChange(quote.id, "normal");
            setLocalPriority("normal");
          }
          break;
        case "reset_priority":
          // Contra-acción de high/urgent -> normal
          if (onPriorityChange) {
            await onPriorityChange(quote.id, "normal");
            setLocalPriority("normal");
          }
          break;
        case "mark_low_priority":
          if (onPriorityChange) {
            await onPriorityChange(quote.id, "low");
            setLocalPriority("low");
          }
          break;

        default:
          break;
      }
    } catch (error) {
      console.error("Error en acción rápida:", error);
    } finally {
      setQuickActionLoading(null);
    }
  };

  const handleShare = async () => {
    const shareText = `Cotización ${quote.quote_id}\nProducto: ${
      quote.product_data?.name
    }\nCliente: ${quote.client_name}\nPrecio: ${formatPrice(
      quote.product_data?.precio
    )}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Cotización ${quote.quote_id}`,
          text: shareText,
        });
      } catch (error) {
        console.log("Error sharing:", error);
      }
    } else {
      navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleContactSupport = () => {
    const whatsappUrl = getWhatsAppURL(
      `Hola, tengo una consulta sobre mi cotización ${quote.quote_id}. ¿Podrían ayudarme?`,
      quote.quote_id
    );
    window.open(whatsappUrl, "_blank");
  };

  if (!quote) return null;

  // ═══════════════════════════════════════════════════════════════════
  // 🎯 DEFINIR ACCIONES DISPONIBLES SEGÚN ESTADO ACTUAL
  // ═══════════════════════════════════════════════════════════════════
  const getQuickActions = () => {
    const actions = [];

    // ══════════════════════════════════════════════════════════════════
    // SECCIÓN: ACCIONES DE PRIORIDAD
    // ══════════════════════════════════════════════════════════════════

    // Si NO es urgente y NO está cerrada -> Puede marcar urgente
    if (!isUrgent && !isClosed) {
      actions.push({
        key: "escalate",
        label: "Marcar Urgente",
        icon: <PriorityHigh />,
        color: "error",
        variant: "outlined",
      });
    }

    // CONTRA-ACCIÓN: Si ES urgente -> Puede quitar urgencia
    if (isUrgent && !isClosed) {
      actions.push({
        key: "unmark_urgent",
        label: "Quitar Urgencia",
        icon: <LowPriority />,
        color: "inherit",
        variant: "outlined",
        isCounterAction: true,
      });
    }

    // Si NO es alta prioridad, NO es urgente y NO está cerrada -> Puede marcar alta
    if (!isHighPriority && !isUrgent && !isClosed) {
      actions.push({
        key: "mark_high",
        label: "Prioridad Alta",
        icon: <Flag />,
        color: "warning",
        variant: "outlined",
      });
    }

    // CONTRA-ACCIÓN: Si es alta prioridad -> Puede volver a normal
    if (isHighPriority && !isClosed) {
      actions.push({
        key: "reset_priority",
        label: "Prioridad Normal",
        icon: <FlagOutlined />,
        color: "primary",
        variant: "outlined",
        isCounterAction: true,
      });
    }

    // ══════════════════════════════════════════════════════════════════
    // SECCIÓN: ACCIONES DE ESTADO
    // ══════════════════════════════════════════════════════════════════

    // Si está pendiente -> Puede marcar contactado o iniciar progreso
    if (isPending) {
      // actions.push({
      //   key: "mark_contacted",
      //   label: "Marcar Contactado",
      //   icon: <Phone />,
      //   color: "info",
      //   variant: "contained",
      // });
      actions.push({
        key: "start_progress",
        label: "Iniciar Progreso",
        icon: <PlayArrow />,
        color: "info",
        variant: "outlined",
      });
    }

    // CONTRA-ACCIÓN: Si está en progreso -> Puede pausar (volver a pendiente)
    if (isInProgress) {
      actions.push({
        key: "pause_to_pending",
        label: "Pausar (Pendiente)",
        icon: <Pause />,
        color: "warning",
        variant: "outlined",
        isCounterAction: true,
      });
    }

    // Si NO está cerrada -> Puede cerrar como exitosa
    if (!isClosed) {
      actions.push({
        key: "mark_completed",
        label: "Cerrar Exitosa",
        icon: <CheckCircleOutline />,
        color: "success",
        variant: "contained",
      });
    }

    // Si NO está cerrada -> Puede rechazar o cancelar
    if (!isClosed) {
      actions.push({
        key: "mark_rejected",
        label: "Rechazar",
        icon: <Cancel />,
        color: "error",
        variant: "outlined",
      });
    }

    // ══════════════════════════════════════════════════════════════════
    // CONTRA-ACCIÓN PRINCIPAL: Si está CERRADA -> Puede reabrir
    // ══════════════════════════════════════════════════════════════════
    if (isClosed) {
      actions.push({
        key: "reopen",
        label: "🔄 Reabrir Cotización",
        icon: <Undo />,
        color: "primary",
        variant: "contained",
        isCounterAction: true,
        highlight: true, // Destacar esta acción
      });
    }

    return actions;
  };

  const quickActions = getQuickActions();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmall}
      TransitionComponent={Fade}
      transitionDuration={300}
      PaperProps={{
        sx: {
          borderRadius: isSmall ? 0 : 3,
          overflow: "hidden",
          maxHeight: isSmall ? "100%" : "90vh",
        },
      }}
    >
      {/* Progress bar cuando está actualizando */}
      {updating && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
          }}
        />
      )}

      {/* Header con gradiente */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          p: { xs: 2, md: 3 },
          position: "relative",
        }}
      >
        {/* Botón cerrar */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            backgroundColor: "rgba(255,255,255,0.1)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.2)",
            },
          }}
        >
          <Close />
        </IconButton>

        {/* Título y badges */}
        <Box sx={{ pr: 5 }}>
          <Typography
            variant={isMobile ? "subtitle2" : "body2"}
            sx={{ opacity: 0.8, mb: 0.5 }}
          >
            {isBackoffice ? "Gestión de Cotización" : "Detalle de Cotización"}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="bold"
              sx={{ fontFamily: "monospace" }}
            >
              {quote.quote_id}
            </Typography>
            <CopyButton
              text={quote.quote_id}
              id={`modal-quote-id-${quote.quote_id}`}
              size="small"
              showFeedback={false}
              sx={{
                color: "white",
                backgroundColor: "rgba(255,255,255,0.15)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
              }}
            />

            {/* Badge de cliente recurrente (solo backoffice) */}
            {isBackoffice && clientMetrics?.isRecurring && (
              <Tooltip
                title={`Cliente con ${clientMetrics.totalQuotes} cotizaciones`}
              >
                <Chip
                  icon={<Repeat sx={{ fontSize: 16 }} />}
                  label="Recurrente"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.warning.light, 0.9),
                    color: theme.palette.warning.dark,
                    fontWeight: "bold",
                  }}
                />
              </Tooltip>
            )}

            {/* Badge de cliente de alto valor (solo backoffice) */}
            {isBackoffice && clientMetrics?.isHighValue && (
              <Tooltip
                title={`Total cotizado: ${formatPrice(
                  clientMetrics.totalValue
                )}`}
              >
                <Chip
                  icon={<Star sx={{ fontSize: 16 }} />}
                  label="Alto Valor"
                  size="small"
                  sx={{
                    backgroundColor: alpha("#FFD700", 0.9),
                    color: "#7D6608",
                    fontWeight: "bold",
                  }}
                />
              </Tooltip>
            )}
          </Box>

          {/* Badges de estado y prioridad - SOLO PARA BACKOFFICE */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {isBackoffice && (
              <>
                <Chip
                  icon={statusConfig.icon}
                  label={statusConfig.label}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.95)",
                    color:
                      theme.palette[statusConfig.color]?.main ||
                      statusConfig.color,
                    fontWeight: "bold",
                    "& .MuiChip-icon": {
                      color:
                        theme.palette[statusConfig.color]?.main ||
                        statusConfig.color,
                    },
                  }}
                />
                <Chip
                  icon={<Flag sx={{ fontSize: 16 }} />}
                  label={priorityConfig.label}
                  size="small"
                  sx={{
                    backgroundColor: alpha(
                      theme.palette[priorityConfig.color]?.main || "#666",
                      0.9
                    ),
                    color: "white",
                    fontWeight: "bold",
                  }}
                />
              </>
            )}
            {timeInfo && (
              <Chip
                icon={<AccessTime sx={{ fontSize: 16 }} />}
                label={timeInfo.relativeTime}
                size="small"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "white",
                }}
              />
            )}
          </Box>
        </Box>

        {/* Indicador de urgencia y SLA (solo backoffice y si es relevante) */}
        {isBackoffice && timeInfo && localStatus === "pending" && (
          <Box sx={{ mt: 2 }}>
            {/* Barra de SLA */}
            <Box sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  SLA de Respuesta (24h)
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: timeInfo.slaBreached
                      ? theme.palette.error.light
                      : "rgba(255,255,255,0.8)",
                    fontWeight: timeInfo.slaBreached ? "bold" : "normal",
                  }}
                >
                  {timeInfo.hoursElapsed.toFixed(0)}h / 24h
                  {timeInfo.slaBreached && " ⚠️ VENCIDO"}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={timeInfo.slaPercentage}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.2)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    backgroundColor: timeInfo.slaBreached
                      ? theme.palette.error.light
                      : timeInfo.slaPercentage > 75
                      ? theme.palette.warning.light
                      : theme.palette.success.light,
                  },
                }}
              />
            </Box>

            {/* Alerta de urgencia */}
            {timeInfo.urgencyLevel !== "low" && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  backgroundColor: alpha(timeInfo.urgencyColor, 0.15),
                  borderRadius: 2,
                  border: `1px solid ${alpha(timeInfo.urgencyColor, 0.3)}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Warning sx={{ color: timeInfo.urgencyColor }} />
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: "white" }}
                  >
                    {timeInfo.urgencyLabel}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.8)" }}
                  >
                    Esta cotización lleva{" "}
                    {timeInfo.diffDays > 0
                      ? `${timeInfo.diffDays} días`
                      : `${timeInfo.diffHours} horas`}{" "}
                    sin respuesta
                  </Typography>
                </Box>
              </Paper>
            )}
          </Box>
        )}
      </Box>

      <DialogContent
        sx={{
          p: { xs: 2, md: 3 },
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
            borderRadius: 4,
          },
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════
            ⚡ ACCIONES RÁPIDAS CON CONTRA-ACCIONES - SOLO BACKOFFICE 
            ═══════════════════════════════════════════════════════════════ */}
        {isBackoffice && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              borderRadius: 2,
              border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <Bolt sx={{ color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" fontWeight="bold" color="primary">
                Acciones Rápidas
              </Typography>
              {isClosed && (
                <Chip
                  label="Cotización cerrada"
                  size="small"
                  color="default"
                  variant="outlined"
                />
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {quickActions.map((action) => (
                <Tooltip
                  key={action.key}
                  title={
                    action.isCounterAction
                      ? `Contra-acción: ${action.label}`
                      : action.label
                  }
                >
                  <Button
                    size="small"
                    variant={action.variant}
                    color={action.color}
                    startIcon={
                      quickActionLoading === action.key ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        action.icon
                      )
                    }
                    onClick={() => handleQuickAction(action.key)}
                    disabled={quickActionLoading !== null}
                    sx={{
                      // Destacar contra-acciones
                      ...(action.isCounterAction && {
                        borderStyle: "dashed",
                      }),
                      // Animación para acción destacada (reabrir)
                      ...(action.highlight && {
                        animation: "pulse 2s infinite",
                        "@keyframes pulse": {
                          "0%": {
                            boxShadow: `0 0 0 0 ${alpha(
                              theme.palette.primary.main,
                              0.4
                            )}`,
                          },
                          "70%": {
                            boxShadow: `0 0 0 8px ${alpha(
                              theme.palette.primary.main,
                              0
                            )}`,
                          },
                          "100%": {
                            boxShadow: `0 0 0 0 ${alpha(
                              theme.palette.primary.main,
                              0
                            )}`,
                          },
                        },
                      }),
                    }}
                  >
                    {action.label}
                  </Button>
                </Tooltip>
              ))}
            </Box>

            {/* Leyenda de contra-acciones */}
            {quickActions.some((a) => a.isCounterAction) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1.5, fontStyle: "italic" }}
              >
                💡 Los botones con borde punteado son contra-acciones que
                revierten el estado actual
              </Typography>
            )}
          </Paper>
        )}

        {/* Sección del Producto */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: productSoldStatus
              ? alpha(theme.palette.error.main, 0.05)
              : alpha(theme.palette.grey[100], 1),
            borderRadius: 2,
            border: productSoldStatus
              ? `2px solid ${theme.palette.error.main}`
              : `1px solid ${theme.palette.divider}`,
            cursor:
              !productSoldStatus && quote.product_data?.slug
                ? "pointer"
                : "default",
            transition: "all 0.2s ease",
            "&:hover":
              !productSoldStatus && quote.product_data?.slug
                ? {
                    transform: "translateY(-2px)",
                    boxShadow: theme.shadows[4],
                  }
                : {},
          }}
          onClick={handleNavigateToProduct}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            {/* Imagen del producto */}
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <SmartImage
                src={getProductImage()}
                alt={quote.product_data?.name}
                fallback={imagePlaceholder}
                style={{
                  width: isMobile ? "100%" : 180,
                  height: isMobile ? 180 : 135,
                  objectFit: "cover",
                  borderRadius: 8,
                  filter: productSoldStatus
                    ? "grayscale(100%) brightness(0.7)"
                    : "none",
                }}
              />
              {productSoldStatus && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%) rotate(-15deg)",
                    backgroundColor: theme.palette.error.main,
                    color: "white",
                    px: 3,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    boxShadow: theme.shadows[4],
                  }}
                >
                  VENDIDO
                </Box>
              )}
              {checkingProduct && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "white",
                    borderRadius: "50%",
                    p: 0.5,
                  }}
                >
                  <CircularProgress size={16} />
                </Box>
              )}
            </Box>

            {/* Info del producto */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.5,
                    }}
                  >
                    <DirectionsCar sx={{ fontSize: 14 }} />
                    Producto Cotizado
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      opacity: productSoldStatus ? 0.6 : 1,
                      lineHeight: 1.2,
                    }}
                  >
                    {quote.product_data?.name || "Producto no disponible"}
                  </Typography>
                </Box>
                {!productSoldStatus && quote.product_data?.slug && (
                  <Tooltip title="Ver producto">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigateToProduct();
                      }}
                    >
                      <OpenInNew />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Detalles del producto */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.5 }}>
                {quote.product_data?.categoria && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Category sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.secondary">
                      {quote.product_data.categoria}
                    </Typography>
                  </Box>
                )}
                {quote.product_data?.brand && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LocalOffer
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {quote.product_data.brand}
                    </Typography>
                  </Box>
                )}
                {quote.product_data?.location && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LocationOn
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {quote.product_data.location}
                    </Typography>
                  </Box>
                )}
                {quote.product_data?.year && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarToday
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Año {quote.product_data.year}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Precio destacado */}
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  borderRadius: 1.5,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <TrendingUp sx={{ color: theme.palette.primary.main }} />
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color="primary"
                  sx={{ opacity: productSoldStatus ? 0.5 : 1 }}
                >
                  {formatPrice(quote.product_data?.precio)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Métricas del Cliente - SOLO BACKOFFICE */}
        {isBackoffice && clientMetrics && clientMetrics.totalQuotes > 1 && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: alpha(theme.palette.info.main, 0.03),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TrendingUp sx={{ color: theme.palette.info.main }} />
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="info.main"
                >
                  Perfil del Cliente
                </Typography>
              </Box>
              <Chip
                label={`${clientMetrics.totalQuotes} cotizaciones`}
                size="small"
                color="info"
                variant="outlined"
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  p: 1.5,
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {clientMetrics.totalQuotes}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Cotizaciones
                </Typography>
              </Box>
              <Box
                sx={{
                  textAlign: "center",
                  p: 1.5,
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {clientMetrics.completedQuotes}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Cerradas
                </Typography>
              </Box>
              <Box
                sx={{
                  textAlign: "center",
                  p: 1.5,
                  backgroundColor: "white",
                  borderRadius: 1,
                  gridColumn: { xs: "span 2", sm: "span 1" },
                }}
              >
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {clientMetrics.conversionRate.toFixed(0)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Conversión
                </Typography>
              </Box>
            </Box>

            {/* Historial de otras cotizaciones */}
            {clientMetrics.otherQuotes.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  startIcon={showHistory ? <ExpandLess /> : <ExpandMore />}
                  endIcon={
                    <Badge
                      badgeContent={clientMetrics.otherQuotes.length}
                      color="info"
                    >
                      <History />
                    </Badge>
                  }
                  onClick={() => setShowHistory(!showHistory)}
                  sx={{ mb: 1 }}
                >
                  {showHistory ? "Ocultar historial" : "Ver otras cotizaciones"}
                </Button>

                <Collapse in={showHistory}>
                  <List
                    dense
                    sx={{ backgroundColor: "white", borderRadius: 1 }}
                  >
                    {clientMetrics.otherQuotes.map((histQuote) => {
                      const histStatus =
                        QUOTE_STATUSES[histQuote.status] ||
                        QUOTE_STATUSES.pending;
                      return (
                        <ListItem key={histQuote.id} divider>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            {histStatus.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              histQuote.product_data?.name || histQuote.quote_id
                            }
                            secondary={`${formatPrice(
                              histQuote.product_data?.precio
                            )} • ${formatServerDate(histQuote.created_at)}`}
                            primaryTypographyProps={{
                              variant: "body2",
                              noWrap: true,
                            }}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              label={histStatus.label}
                              size="small"
                              color={histStatus.color}
                              variant="outlined"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            )}
          </Paper>
        )}

        {/* Grid de información */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            mb: 3,
          }}
        >
          {/* Información del Cliente */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: "100%",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <Avatar
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              >
                <Person />
              </Avatar>
              <Typography variant="subtitle1" fontWeight="bold">
                Información del Cliente
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Nombre */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Person sx={{ fontSize: 20, color: "text.secondary" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Nombre
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {quote.client_name}
                  </Typography>
                </Box>
              </Box>

              {/* Email */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Email sx={{ fontSize: 20, color: "text.secondary" }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    Correo electrónico
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      wordBreak: "break-all",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {quote.client_email}
                    <CopyButton
                      text={quote.client_email}
                      id={`email-${quote.id}`}
                      size="small"
                      showFeedback={false}
                    />
                  </Typography>
                </Box>
              </Box>

              {/* Teléfono */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Phone sx={{ fontSize: 20, color: "text.secondary" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Teléfono
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {quote.client_phone}
                    <CopyButton
                      text={quote.client_phone}
                      id={`phone-${quote.id}`}
                      size="small"
                      showFeedback={false}
                    />
                  </Typography>
                </Box>
              </Box>

              {/* Empresa (si existe) */}
              {quote.company_business && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Business sx={{ fontSize: 20, color: "text.secondary" }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Empresa
                    </Typography>
                    <Typography variant="body1">
                      {quote.company_business}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* RUT (si existe) */}
              {quote.client_rut && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <AttachMoney sx={{ fontSize: 20, color: "text.secondary" }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      RUT
                    </Typography>
                    <Typography variant="body1">{quote.client_rut}</Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Acciones rápidas de contacto - SOLO BACKOFFICE */}
            {isBackoffice && (
              <Box sx={{ display: "flex", gap: 1, mt: 2.5, flexWrap: "wrap" }}>
                <Tooltip title="Enviar WhatsApp">
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<WhatsApp />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWhatsAppClient();
                    }}
                    sx={{
                      backgroundColor: "#25D366",
                      "&:hover": { backgroundColor: "#128C7E" },
                      flex: 1,
                      minWidth: "fit-content",
                    }}
                  >
                    WhatsApp
                  </Button>
                </Tooltip>
                <Tooltip title="Enviar Email">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Email />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEmailClient();
                    }}
                    sx={{ flex: 1, minWidth: "fit-content" }}
                  >
                    Email
                  </Button>
                </Tooltip>
                <Tooltip title="Llamar">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Phone />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCallClient();
                    }}
                    sx={{ flex: 1, minWidth: "fit-content" }}
                  >
                    Llamar
                  </Button>
                </Tooltip>
              </Box>
            )}
          </Paper>

          {/* Información de la Cotización */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: "100%",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <Avatar
                sx={{
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.main,
                }}
              >
                <CalendarToday />
              </Avatar>
              <Typography variant="subtitle1" fontWeight="bold">
                Información de la Cotización
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Fecha de creación */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CalendarToday sx={{ fontSize: 20, color: "text.secondary" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Fecha de creación
                  </Typography>
                  <Typography variant="body1">
                    {timeInfo?.formattedCreated || "No disponible"}
                  </Typography>
                </Box>
              </Box>

              {/* Última actualización - solo backoffice */}
              {isBackoffice && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Update sx={{ fontSize: 20, color: "text.secondary" }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Última actualización
                    </Typography>
                    <Typography variant="body1">
                      {timeInfo?.formattedUpdated || "No disponible"}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Tiempo transcurrido - solo backoffice */}
              {isBackoffice && timeInfo && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Timer sx={{ fontSize: 20, color: "text.secondary" }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Tiempo transcurrido
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {timeInfo.diffDays > 0
                        ? `${timeInfo.diffDays} día${
                            timeInfo.diffDays > 1 ? "s" : ""
                          } y ${timeInfo.diffHours} hora${
                            timeInfo.diffHours !== 1 ? "s" : ""
                          }`
                        : `${timeInfo.diffHours} hora${
                            timeInfo.diffHours !== 1 ? "s" : ""
                          }`}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Estado descriptivo - solo backoffice */}
              {isBackoffice && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    backgroundColor: alpha(
                      theme.palette[statusConfig.color]?.main ||
                        theme.palette.grey[500],
                      0.08
                    ),
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {statusConfig.icon}
                  <Typography variant="body2" color="text.secondary">
                    {statusConfig.description}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Mensaje del cliente (si existe) */}
        {quote.message && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.warning.main, 0.03),
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
            >
              <Avatar
                sx={{
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  color: theme.palette.warning.main,
                  width: 32,
                  height: 32,
                }}
              >
                <Message sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="subtitle1" fontWeight="bold">
                Mensaje del Cliente
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                whiteSpace: "pre-wrap",
                p: 2,
                backgroundColor: "white",
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              {quote.message}
            </Typography>
          </Paper>
        )}

        {/* Controles de Estado y Prioridad (solo backoffice) */}
        {isBackoffice && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.grey[100], 0.5),
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Gestionar Cotización
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={localStatus}
                  onChange={handleStatusChange}
                  label="Estado"
                  disabled={updating}
                >
                  {Object.entries(QUOTE_STATUSES).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {config.icon}
                        <span>{config.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Prioridad</InputLabel>
                <Select
                  value={localPriority}
                  onChange={handlePriorityChange}
                  label="Prioridad"
                  disabled={updating}
                >
                  {Object.entries(PRIORITIES).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Flag
                          sx={{ color: theme.palette[config.color]?.main }}
                        />
                        <span>{config.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>
        )}
      </DialogContent>

      {/* Footer con acciones */}
      <Divider />
      <DialogActions
        sx={{
          p: { xs: 2, md: 2.5 },
          gap: 1,
          flexWrap: "wrap",
          justifyContent: { xs: "center", sm: "flex-end" },
        }}
      >
        {/* Botón compartir */}
        {/* <Tooltip title={copied ? "¡Copiado!" : "Compartir"}>
          <Button
            variant="text"
            color="inherit"
            startIcon={copied ? <CheckCircle color="success" /> : <Share />}
            onClick={handleShare}
            sx={{ mr: "auto", display: { xs: "none", sm: "flex" } }}
          >
            {copied ? "Copiado" : "Compartir"}
          </Button>
        </Tooltip> */}

        {/* Botón contactar soporte (solo para usuarios) */}
        {!isBackoffice && (
          <Button
            variant="outlined"
            startIcon={<WhatsApp />}
            onClick={handleContactSupport}
            sx={{
              borderColor: "#25D366",
              color: "#25D366",
              "&:hover": {
                borderColor: "#128C7E",
                backgroundColor: alpha("#25D366", 0.04),
              },
            }}
          >
            Consultar
          </Button>
        )}

        {/* Botón descargar PDF */}
        {/* <Button
          variant="outlined"
          color="primary"
          startIcon={
            downloadingPdf ? <CircularProgress size={18} /> : <PictureAsPdf />
          }
          onClick={handleDownloadPdf}
          disabled={downloadingPdf}
        >
          {downloadingPdf ? "Descargando..." : "Descargar PDF"}
        </Button> */}

        {/* Botón ver producto */}
        {!productSoldStatus && quote.product_data?.slug && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<OpenInNew />}
            onClick={handleNavigateToProduct}
          >
            Ver Producto
          </Button>
        )}

        {/* Botón cerrar */}
        <Button
          variant={
            productSoldStatus || !quote.product_data?.slug
              ? "contained"
              : "text"
          }
          onClick={onClose}
        >
          Cerrar
        </Button>
      </DialogActions>

      {/* Error de PDF */}
      {pdfError && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography
            variant="caption"
            color="error"
            textAlign="center"
            display="block"
          >
            {pdfError}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default QuoteDetailModal;
